import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Loader2, User, Briefcase, CreditCard, Clock, FileCheck, Trophy, LogOut, ShieldOff, AlertTriangle, Upload, Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ROLES, ROLE_LABELS, getAssignableRoles } from '../config/roles';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEPARTMENTS = ['Engineering', 'HR', 'Finance', 'Marketing', 'Operations', 'Sales', 'Support', 'Admin'];
const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Intern'];
const EMPLOYEE_STATUSES = ['Active', 'On Notice', 'Relieved', 'Absconded'];
const GENDERS = ['Male', 'Female', 'Other'];
const SHIFT_TYPES = ['General', 'Morning', 'Evening', 'Night', 'Rotational'];
const ATTENDANCE_MODES = ['Biometric', 'App', 'Manual'];
const DISABLE_REASONS = [
  'Left company',
  'Terminated',
  'Security concern',
  'Extended leave',
  'Contract ended',
  'Other'
];

export default function EmployeeFormPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // For edit mode
  const isEditMode = !!id;
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingEmployee, setFetchingEmployee] = useState(false);
  const [managers, setManagers] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Photo upload state
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Get assignable roles based on current user's role
  const currentUserRole = user?.role || 'admin';
  const assignableRoles = getAssignableRoles(currentUserRole);
  
  // Check if current user can disable employees (only HR and Owner)
  const canDisableEmployee = ['owner', 'hr'].includes(currentUserRole);
  
  // Form state
  const [formData, setFormData] = useState({
    // Employee ID & Password (New)
    employee_id: '',
    password: '',
    
    // Role
    role: 'admin',
    
    // Basic Info
    first_name: '',
    last_name: '',
    gender: 'Male',
    date_of_birth: '',
    mobile_number: '',
    email: '',
    photograph_url: '',
    
    // Address
    current_address: { street: '', city: '', state: '', pincode: '', country: 'India' },
    permanent_address: { street: '', city: '', state: '', pincode: '', country: 'India' },
    
    // Emergency Contact
    emergency_contact: { name: '', phone: '', relationship: '' },
    
    // Employment Details
    department: '',
    designation: '',
    reporting_manager_id: '',
    employment_type: 'Full-time',
    date_of_joining: '',
    work_location: 'Head Office',
    employee_status: 'Active',
    probation_end_date: '',
    
    // Bank Details
    bank_details: { bank_name: '', account_number: '', ifsc_code: '', branch: '' },
    
    // Payroll
    payroll: {
      salary_type: 'Monthly',
      basic_salary: 0,
      hra: 0,
      travel_allowance: 0,
      other_allowances: 0,
      pf_deduction: 0,
      esi_deduction: 0,
      tds_deduction: 0,
      other_deductions: 0,
    },
    
    // Attendance
    attendance_settings: {
      shift_type: 'General',
      work_timings: '9:00 AM - 6:00 PM',
      attendance_mode: 'App',
      weekly_off: 'Sunday',
      leave_policy: 'Standard',
    },
    
    // Leave Balance
    leave_balance: {
      casual_leave: 12,
      sick_leave: 6,
      privilege_leave: 15,
      used_cl: 0,
      used_sl: 0,
      used_pl: 0,
    },
    
    // Compliance
    compliance_documents: {
      aadhaar_number: '',
      pan_number: '',
      uan_number: '',
      esi_number: '',
      nda_signed: false,
      background_verification_status: 'Pending',
    },
    
    // Performance
    performance: {
      skill_set: [],
      performance_rating: null,
      last_appraisal_date: '',
      next_appraisal_due: '',
      training_programs: [],
      certifications: [],
    },
    
    // System
    is_admin: false,
    notes: '',
    
    // Account Disable (HR/Owner only)
    is_disabled: false,
    disabled_reason: '',
  });

  useEffect(() => {
    fetchManagers();
    if (isEditMode && id) {
      fetchEmployeeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const fetchEmployeeData = async () => {
    setFetchingEmployee(true);
    try {
      const response = await axios.get(`${API_URL}/api/employees/${id}`);
      const emp = response.data;
      setFormData(prev => ({
        ...prev,
        employee_id: emp.employee_id || '',
        role: emp.role || 'admin',
        first_name: emp.first_name || '',
        last_name: emp.last_name || '',
        gender: emp.gender || 'Male',
        date_of_birth: emp.date_of_birth || '',
        mobile_number: emp.mobile_number || '',
        email: emp.email || '',
        photograph_url: emp.photograph_url || '',
        current_address: emp.current_address || prev.current_address,
        permanent_address: emp.permanent_address || prev.permanent_address,
        emergency_contact: emp.emergency_contact || prev.emergency_contact,
        department: emp.department || '',
        designation: emp.designation || '',
        reporting_manager_id: emp.reporting_manager_id || '',
        employment_type: emp.employment_type || 'Full-time',
        date_of_joining: emp.date_of_joining || '',
        work_location: emp.work_location || 'Head Office',
        employee_status: emp.employee_status || 'Active',
        probation_end_date: emp.probation_end_date || '',
        bank_details: emp.bank_details || prev.bank_details,
        payroll: emp.payroll || prev.payroll,
        attendance_settings: emp.attendance_settings || prev.attendance_settings,
        leave_balance: emp.leave_balance || prev.leave_balance,
        compliance_documents: emp.compliance_documents || prev.compliance_documents,
        performance: emp.performance || prev.performance,
        is_admin: emp.is_admin || false,
        notes: emp.notes || '',
        is_disabled: emp.is_disabled || false,
        disabled_reason: emp.disabled_reason || '',
      }));
      // Set photo preview if employee has an existing photo
      if (emp.photograph_url) {
        setPhotoPreview(null); // Clear preview, let the actual URL display
      }
    } catch (error) {
      toast.error('Failed to load employee data');
      navigate('/employees');
    } finally {
      setFetchingEmployee(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/managers`);
      setManagers(response.data.managers || []);
    } catch {
      setManagers([]);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  // Handle photo file selection and upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WEBP, or GIF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setPhotoPreview(previewUrl);

    // Upload the file
    setUploadingPhoto(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/employee-photo`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Set the URL from the response
      const photoUrl = `${API_URL}${response.data.url}`;
      handleChange('photograph_url', photoUrl);
      toast.success('Photo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Remove photo
  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    handleChange('photograph_url', '');
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.mobile_number || !formData.department || !formData.designation || !formData.date_of_joining) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        // Update employee
        await axios.put(`${API_URL}/api/employees/${id}`, formData);
        toast.success('Employee updated successfully');
      } else {
        // Create employee
        await axios.post(`${API_URL}/api/employees`, formData);
        toast.success('Employee created successfully');
      }
      navigate('/employees');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} employee`);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingEmployee) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div data-testid="employee-form-page" className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/employees')}
          className="text-slate-400"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white">{isEditMode ? 'Edit Employee' : 'Add New Employee'}</h1>
          <p className="text-sm text-slate-500">{isEditMode ? 'Update employee details' : 'Fill in the employee details below'}</p>
        </div>
      </div>

      {/* Account Disabled Warning Banner */}
      {formData.is_disabled && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-semibold">Account Disabled</p>
            <p className="text-red-300 text-sm">This employee cannot access the admin panel. Reason: {formData.disabled_reason || 'Not specified'}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700 p-1">
            <TabsTrigger value="basic" className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900">
              <User className="w-4 h-4 mr-2" />
              Basic Info
            </TabsTrigger>
            <TabsTrigger value="employment" className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900">
              <Briefcase className="w-4 h-4 mr-2" />
              Employment
            </TabsTrigger>
            <TabsTrigger value="payroll" className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900">
              <CreditCard className="w-4 h-4 mr-2" />
              Payroll
            </TabsTrigger>
            <TabsTrigger value="attendance" className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900">
              <Clock className="w-4 h-4 mr-2" />
              Attendance
            </TabsTrigger>
            <TabsTrigger value="compliance" className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900">
              <FileCheck className="w-4 h-4 mr-2" />
              Compliance
            </TabsTrigger>
            <TabsTrigger value="performance" className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900">
              <Trophy className="w-4 h-4 mr-2" />
              Performance
            </TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-6">
            {/* Employee ID & Credentials */}
            <Card className="bg-slate-900/50 border-slate-800 border-l-4 border-l-green-500">
              <CardHeader>
                <CardTitle className="text-white">Employee Credentials</CardTitle>
                <CardDescription>Employee ID and login credentials</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Employee ID</Label>
                  <Input
                    value={formData.employee_id}
                    onChange={(e) => handleChange('employee_id', e.target.value)}
                    placeholder="e.g., LP-001 (auto-generated if empty)"
                    className="bg-slate-800 border-slate-700 font-mono"
                    data-testid="employee-id-input"
                  />
                  <p className="text-xs text-slate-500">Leave blank for auto-generated ID (EMP0001, EMP0002...)</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Set employee login password"
                    className="bg-slate-800 border-slate-700"
                    data-testid="password-input"
                  />
                  <p className="text-xs text-slate-500">Default: Welcome@123 if not set</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Personal Information</CardTitle>
                <CardDescription>Basic identification details</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                    required
                    data-testid="last-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                    data-testid="dob-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Mobile Number *</Label>
                  <Input
                    value={formData.mobile_number}
                    onChange={(e) => handleChange('mobile_number', e.target.value)}
                    placeholder="+91 98765 43210"
                    className="bg-slate-800 border-slate-700"
                    required
                    data-testid="mobile-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="email@lunipin.com"
                    className="bg-slate-800 border-slate-700"
                    required
                    data-testid="email-input"
                  />
                </div>
                
                {/* Employee Photo Upload */}
                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <Label className="text-slate-300">Employee Photo</Label>
                  <div className="flex items-start gap-6">
                    {/* Photo Preview */}
                    <div className="relative">
                      <div 
                        onClick={() => !uploadingPhoto && photoInputRef.current?.click()}
                        className="w-32 h-32 rounded-full border-2 border-dashed border-slate-600 hover:border-green-500 flex items-center justify-center cursor-pointer overflow-hidden bg-slate-800 transition-colors"
                      >
                        {uploadingPhoto ? (
                          <Loader2 className="w-8 h-8 text-green-400 animate-spin" />
                        ) : (photoPreview || formData.photograph_url) ? (
                          <img 
                            src={photoPreview || formData.photograph_url} 
                            alt="Employee" 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : (
                          <div className="flex flex-col items-center text-slate-500">
                            <Camera className="w-8 h-8 mb-1" />
                            <span className="text-xs">Upload Photo</span>
                          </div>
                        )}
                        {(photoPreview || formData.photograph_url) && (
                          <div className="hidden flex-col items-center text-slate-500">
                            <Camera className="w-8 h-8 mb-1" />
                            <span className="text-xs">Upload Photo</span>
                          </div>
                        )}
                      </div>
                      {(photoPreview || formData.photograph_url) && !uploadingPhoto && (
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Upload Instructions */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => photoInputRef.current?.click()}
                          disabled={uploadingPhoto}
                          className="border-green-500 text-green-400 hover:bg-green-500/10"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingPhoto ? 'Uploading...' : 'Choose Photo'}
                        </Button>
                        <span className="text-xs text-slate-500">or drag & drop</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        Supported formats: JPEG, PNG, WEBP, GIF<br />
                        Max file size: 5MB<br />
                        Recommended: Square image (1:1 ratio)
                      </p>
                      
                      {/* Optional URL input for fallback */}
                      <div className="pt-2">
                        <Label className="text-slate-400 text-xs">Or enter photo URL</Label>
                        <Input
                          value={formData.photograph_url}
                          onChange={(e) => {
                            handleChange('photograph_url', e.target.value);
                            setPhotoPreview(null);
                          }}
                          placeholder="https://..."
                          className="bg-slate-800 border-slate-700 mt-1 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Hidden file input */}
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Current Address</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-slate-300">Street Address</Label>
                  <Input
                    value={formData.current_address.street}
                    onChange={(e) => handleNestedChange('current_address', 'street', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">City</Label>
                  <Input
                    value={formData.current_address.city}
                    onChange={(e) => handleNestedChange('current_address', 'city', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">State</Label>
                  <Input
                    value={formData.current_address.state}
                    onChange={(e) => handleNestedChange('current_address', 'state', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Pincode</Label>
                  <Input
                    value={formData.current_address.pincode}
                    onChange={(e) => handleNestedChange('current_address', 'pincode', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Country</Label>
                  <Input
                    value={formData.current_address.country}
                    onChange={(e) => handleNestedChange('current_address', 'country', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Emergency Contact</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Contact Name</Label>
                  <Input
                    value={formData.emergency_contact.name}
                    onChange={(e) => handleNestedChange('emergency_contact', 'name', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone Number</Label>
                  <Input
                    value={formData.emergency_contact.phone}
                    onChange={(e) => handleNestedChange('emergency_contact', 'phone', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Relationship</Label>
                  <Input
                    value={formData.emergency_contact.relationship}
                    onChange={(e) => handleNestedChange('emergency_contact', 'relationship', e.target.value)}
                    placeholder="e.g., Spouse, Parent"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Employment Tab */}
          <TabsContent value="employment" className="space-y-6">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Employment Details</CardTitle>
                <CardDescription>Role, hierarchy, and status information</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Department *</Label>
                  <Select value={formData.department} onValueChange={(v) => handleChange('department', v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Designation *</Label>
                  <Input
                    value={formData.designation}
                    onChange={(e) => handleChange('designation', e.target.value)}
                    placeholder="e.g., Software Engineer"
                    className="bg-slate-800 border-slate-700"
                    required
                    data-testid="designation-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Reporting Manager</Label>
                  <Select value={formData.reporting_manager_id || "none"} onValueChange={(v) => handleChange('reporting_manager_id', v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="none">None</SelectItem>
                      {managers.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.first_name} {m.last_name} - {m.designation}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Employment Type</Label>
                  <Select value={formData.employment_type} onValueChange={(v) => handleChange('employment_type', v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {EMPLOYMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Date of Joining *</Label>
                  <Input
                    type="date"
                    value={formData.date_of_joining}
                    onChange={(e) => handleChange('date_of_joining', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                    required
                    data-testid="joining-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Work Location</Label>
                  <Input
                    value={formData.work_location}
                    onChange={(e) => handleChange('work_location', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Employee Status</Label>
                  <Select value={formData.employee_status} onValueChange={(v) => handleChange('employee_status', v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {EMPLOYEE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Probation End Date</Label>
                  <Input
                    type="date"
                    value={formData.probation_end_date}
                    onChange={(e) => handleChange('probation_end_date', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Initial Password</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Leave blank for default"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Admin Access & Role</CardTitle>
                <CardDescription>Configure dashboard access and role permissions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-300">Grant Admin Access</p>
                    <p className="text-sm text-slate-500">Allow this employee to access the admin dashboard</p>
                  </div>
                  <Switch
                    checked={formData.is_admin}
                    onCheckedChange={(v) => handleChange('is_admin', v)}
                  />
                </div>
                
                {formData.is_admin && (
                  <div className="space-y-2 pt-4 border-t border-slate-700">
                    <Label className="text-slate-300">Employee Role *</Label>
                    <Select value={formData.role} onValueChange={(v) => handleChange('role', v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="role-select">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {assignableRoles.map(role => (
                          <SelectItem key={role} value={role}>
                            {ROLE_LABELS[role]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {currentUserRole === 'owner' 
                        ? 'As Owner, you can assign any role including Owner.' 
                        : 'As HR, you can assign roles except Owner.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Account Security - Only for HR/Owner in Edit Mode */}
            {isEditMode && canDisableEmployee && (
              <Card className={`border-slate-800 ${formData.is_disabled ? 'bg-red-900/20 border-red-500/50' : 'bg-slate-900/50'}`}>
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShieldOff className="w-5 h-5 text-red-400" />
                    Account Security
                  </CardTitle>
                  <CardDescription>Disable employee access for security or HR reasons</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-300">Disable Account</p>
                      <p className="text-sm text-slate-500">Prevent this employee from logging into the admin panel</p>
                    </div>
                    <Switch
                      checked={formData.is_disabled}
                      onCheckedChange={(v) => handleChange('is_disabled', v)}
                      className="data-[state=checked]:bg-red-500"
                    />
                  </div>
                  
                  {formData.is_disabled && (
                    <div className="space-y-2 pt-4 border-t border-slate-700">
                      <Label className="text-slate-300">Reason for Disabling *</Label>
                      <Select value={formData.disabled_reason || "none"} onValueChange={(v) => handleChange('disabled_reason', v === "none" ? "" : v)}>
                        <SelectTrigger className="bg-slate-800 border-slate-700">
                          <SelectValue placeholder="Select reason" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="none">Select a reason</SelectItem>
                          {DISABLE_REASONS.map(reason => (
                            <SelectItem key={reason} value={reason}>
                              {reason}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-red-400">
                        Warning: This employee will not be able to login until the account is re-enabled.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Payroll Tab */}
          <TabsContent value="payroll" className="space-y-6">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Bank Details</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Bank Name</Label>
                  <Input
                    value={formData.bank_details.bank_name}
                    onChange={(e) => handleNestedChange('bank_details', 'bank_name', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Account Number</Label>
                  <Input
                    value={formData.bank_details.account_number}
                    onChange={(e) => handleNestedChange('bank_details', 'account_number', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">IFSC Code</Label>
                  <Input
                    value={formData.bank_details.ifsc_code}
                    onChange={(e) => handleNestedChange('bank_details', 'ifsc_code', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Branch</Label>
                  <Input
                    value={formData.bank_details.branch}
                    onChange={(e) => handleNestedChange('bank_details', 'branch', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Salary & Compensation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Salary Type</Label>
                    <Select value={formData.payroll.salary_type} onValueChange={(v) => handleNestedChange('payroll', 'salary_type', v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="Monthly">Monthly</SelectItem>
                        <SelectItem value="Hourly">Hourly</SelectItem>
                        <SelectItem value="Daily">Daily</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Basic Salary (₹)</Label>
                    <Input
                      type="number"
                      value={formData.payroll.basic_salary}
                      onChange={(e) => handleNestedChange('payroll', 'basic_salary', parseFloat(e.target.value) || 0)}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">HRA (₹)</Label>
                    <Input
                      type="number"
                      value={formData.payroll.hra}
                      onChange={(e) => handleNestedChange('payroll', 'hra', parseFloat(e.target.value) || 0)}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Travel Allowance (₹)</Label>
                    <Input
                      type="number"
                      value={formData.payroll.travel_allowance}
                      onChange={(e) => handleNestedChange('payroll', 'travel_allowance', parseFloat(e.target.value) || 0)}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Other Allowances (₹)</Label>
                    <Input
                      type="number"
                      value={formData.payroll.other_allowances}
                      onChange={(e) => handleNestedChange('payroll', 'other_allowances', parseFloat(e.target.value) || 0)}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                <div className="border-t border-slate-700 pt-4">
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Deductions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">PF (₹)</Label>
                      <Input
                        type="number"
                        value={formData.payroll.pf_deduction}
                        onChange={(e) => handleNestedChange('payroll', 'pf_deduction', parseFloat(e.target.value) || 0)}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">ESI (₹)</Label>
                      <Input
                        type="number"
                        value={formData.payroll.esi_deduction}
                        onChange={(e) => handleNestedChange('payroll', 'esi_deduction', parseFloat(e.target.value) || 0)}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">TDS (₹)</Label>
                      <Input
                        type="number"
                        value={formData.payroll.tds_deduction}
                        onChange={(e) => handleNestedChange('payroll', 'tds_deduction', parseFloat(e.target.value) || 0)}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Other (₹)</Label>
                      <Input
                        type="number"
                        value={formData.payroll.other_deductions}
                        onChange={(e) => handleNestedChange('payroll', 'other_deductions', parseFloat(e.target.value) || 0)}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="space-y-6">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Attendance Settings</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Shift Type</Label>
                  <Select value={formData.attendance_settings.shift_type} onValueChange={(v) => handleNestedChange('attendance_settings', 'shift_type', v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {SHIFT_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Work Timings</Label>
                  <Input
                    value={formData.attendance_settings.work_timings}
                    onChange={(e) => handleNestedChange('attendance_settings', 'work_timings', e.target.value)}
                    placeholder="9:00 AM - 6:00 PM"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Attendance Mode</Label>
                  <Select value={formData.attendance_settings.attendance_mode} onValueChange={(v) => handleNestedChange('attendance_settings', 'attendance_mode', v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {ATTENDANCE_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Weekly Off</Label>
                  <Input
                    value={formData.attendance_settings.weekly_off}
                    onChange={(e) => handleNestedChange('attendance_settings', 'weekly_off', e.target.value)}
                    placeholder="Sunday"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Leave Policy</Label>
                  <Input
                    value={formData.attendance_settings.leave_policy}
                    onChange={(e) => handleNestedChange('attendance_settings', 'leave_policy', e.target.value)}
                    placeholder="Standard"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Leave Balance</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Casual Leave (CL)</Label>
                  <Input
                    type="number"
                    value={formData.leave_balance.casual_leave}
                    onChange={(e) => handleNestedChange('leave_balance', 'casual_leave', parseInt(e.target.value) || 0)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Sick Leave (SL)</Label>
                  <Input
                    type="number"
                    value={formData.leave_balance.sick_leave}
                    onChange={(e) => handleNestedChange('leave_balance', 'sick_leave', parseInt(e.target.value) || 0)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Privilege Leave (PL)</Label>
                  <Input
                    type="number"
                    value={formData.leave_balance.privilege_leave}
                    onChange={(e) => handleNestedChange('leave_balance', 'privilege_leave', parseInt(e.target.value) || 0)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance" className="space-y-6">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Identity & Tax Documents</CardTitle>
                <CardDescription>Critical for audits and legal compliance</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Aadhaar Number</Label>
                  <Input
                    value={formData.compliance_documents.aadhaar_number}
                    onChange={(e) => handleNestedChange('compliance_documents', 'aadhaar_number', e.target.value)}
                    placeholder="XXXX XXXX XXXX"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">PAN Number</Label>
                  <Input
                    value={formData.compliance_documents.pan_number}
                    onChange={(e) => handleNestedChange('compliance_documents', 'pan_number', e.target.value)}
                    placeholder="ABCDE1234F"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">UAN / PF Number</Label>
                  <Input
                    value={formData.compliance_documents.uan_number}
                    onChange={(e) => handleNestedChange('compliance_documents', 'uan_number', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">ESI Number</Label>
                  <Input
                    value={formData.compliance_documents.esi_number}
                    onChange={(e) => handleNestedChange('compliance_documents', 'esi_number', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Background Verification</Label>
                  <Select value={formData.compliance_documents.background_verification_status} onValueChange={(v) => handleNestedChange('compliance_documents', 'background_verification_status', v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Verified">Verified</SelectItem>
                      <SelectItem value="Failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-800 rounded-sm border border-slate-700">
                  <Label className="text-slate-300">NDA Signed</Label>
                  <Switch
                    checked={formData.compliance_documents.nda_signed}
                    onCheckedChange={(v) => handleNestedChange('compliance_documents', 'nda_signed', v)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Skills & Competencies</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Skill Set (comma separated)</Label>
                  <Textarea
                    value={formData.performance.skill_set.join(', ')}
                    onChange={(e) => handleNestedChange('performance', 'skill_set', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="e.g., React, Node.js, Python, Project Management"
                    className="bg-slate-800 border-slate-700 min-h-[80px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Certifications (comma separated)</Label>
                  <Textarea
                    value={formData.performance.certifications.join(', ')}
                    onChange={(e) => handleNestedChange('performance', 'certifications', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="e.g., AWS Certified, PMP, Scrum Master"
                    className="bg-slate-800 border-slate-700 min-h-[80px]"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Appraisal Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Performance Rating (1-5)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    value={formData.performance.performance_rating || ''}
                    onChange={(e) => handleNestedChange('performance', 'performance_rating', parseFloat(e.target.value) || null)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Last Appraisal Date</Label>
                  <Input
                    type="date"
                    value={formData.performance.last_appraisal_date}
                    onChange={(e) => handleNestedChange('performance', 'last_appraisal_date', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Next Appraisal Due</Label>
                  <Input
                    type="date"
                    value={formData.performance.next_appraisal_due}
                    onChange={(e) => handleNestedChange('performance', 'next_appraisal_due', e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Any additional notes or remarks..."
                  className="bg-slate-800 border-slate-700 min-h-[100px]"
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Submit Button */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-800">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/employees')}
            className="text-slate-400"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-green-500 hover:bg-green-600 text-slate-900 font-bold shadow-[0_0_15px_rgba(34,197,94,0.3)]"
            data-testid="submit-employee-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isEditMode ? 'Update Employee' : 'Create Employee'}
          </Button>
        </div>
      </form>
    </div>
  );
}
